/**
 * Export-Adapter: DMRZ-Fahrtenimport, CSV-Format v1.1 (Stand 01.09.2024).
 *
 * Konzept: nextcab_mobile/docs/general/krankenfahrten_dmrz_abrechnung_konzept.md
 * §4.3.3 (Spaltenliste + Feld-Mapping) und §6 Phase 1.
 *
 * Was dieser Adapter NICHT tut: Preise liefern. DMRZ bewertet jeden Fall aus dem
 * beim Kunden hinterlegten Kassenvertrag — wir liefern Fahrt-, Patienten- und
 * Verordnungsdaten samt Besetzt-Kilometern. `GESAMT` geht deshalb bewusst als 0
 * hinaus (die Beispieldatei von DMRZ macht es genauso), und eine bei uns
 * *kalkulierte* Zuzahlung landet nicht in `ZUZ` — beides wäre ein erfundener
 * Kassenbetrag (Guardrail §8.7).
 *
 * ⚠️ Offene Formatpunkte: Die Spezifikation v1.1 und die mitgelieferte
 * Beispieldatei widersprechen sich an vier Stellen (Zeichensatz, Quoting,
 * Dauer-Format, Header-Schreibweise) — die Klärung steht seit dem 16.07.2026
 * bei DMRZ aus (§8.1). Deshalb sind genau diese Punkte über `dmrzFormat` in
 * `appSettings/medicalBilling` umschaltbar, statt hier fest verdrahtet zu sein:
 * Beim Einrichtungsgespräch lässt sich die richtige Variante ohne Code-Änderung
 * einstellen.
 */

/** Kanonische Spaltenreihenfolge (76 Spalten, feste Reihenfolge laut Spez). */
export const DMRZ_COLUMNS = [
  'Auftrags ID', 'BEF', 'V_Nachname', 'V_Vorname', 'V_GebDatum',
  'V_Strasse', 'V_Hausnummer', 'V_PLZ', 'V_Ort', 'Vers_AdrZusatz', 'V_Land',
  'V_IK_Kasse', 'V_Vers_Nr', 'V_Status',
  'DATUM', 'Uhrzeit', 'Dauer', 'WAGEN', 'KM', 'WARTEN', 'GESAMT', 'ZUZ', 'STEUER',
  'A_STRASSE', 'A_PLZ', 'A_ORT', 'A_Zusatz', 'A_LAND',
  'Z_STRASSE', 'Z_PLZ', 'Z_ORT', 'Z_Zusatz', 'Z_LAND',
  'Fahrttyp', 'Auftragsnummer Hinfahrt', 'Bemerkung', 'Kundenart',
  'Serienfahrtnummer', 'LEB IK', 'LEB Name',
  'VODatum', 'KeinVODatum', 'ZZBelegdaten',
  'BVG', 'Unfallfolge', 'awb', 'VOSonstiges',
  'BSNR', 'LANR',
  'keinGen', 'GenDatum', 'Gengueltigvon', 'gengueltigbis', 'genKennz', 'GenArt',
  'Transportkategorie',
  'TPE1', 'TPE2', 'TPE3', 'TPE4', 'TPE5', 'TPE6', 'TPE7', 'TPE8', 'TPE9', 'TPE10',
  'TPE11', 'TPE12', 'TPE13', 'TPE14', 'TPE15', 'TPE16', 'TPE17', 'TPE18', 'TPE19', 'TPE20'
];

/**
 * Abweichende Schreibweisen aus der DMRZ-Beispieldatei. Die Datei enthält
 * offensichtliche Eigenheiten (`A_STRAssE`, Pflichtfeld-Sternchen im Header,
 * `TPW19` statt `TPE19`). Da laut Spezifikation die *Reihenfolge* zählt und
 * nicht der Spaltenname, exportieren wir standardmäßig die saubere Variante —
 * wer beim Einrichten hört „bitte exakt wie im Beispiel", stellt um.
 */
const SAMPLE_HEADER_OVERRIDES = {
  ZUZ: 'ZUZ *',
  STEUER: 'STEUER *',
  A_STRASSE: 'A_STRAssE',
  A_PLZ: 'A_PLZ *',
  Z_STRASSE: 'Z_STRAssE',
  Z_PLZ: 'Z_PLZ *',
  TPE19: 'TPW19'
};

export const DEFAULT_DMRZ_FORMAT = {
  // Spez sagt UTF-8, die Beispieldatei ist Latin-1 kodiert.
  encoding: 'utf-8',
  // Spez sagt „Feldinhalte in Anführungszeichen", die Beispieldatei nutzt keine.
  quoteFields: true,
  // Spez sagt hh:mm, die Beispieldatei liefert ganze Minuten.
  durationFormat: 'hhmm',
  headerVariant: 'spec',
  // Welche unserer beiden Wartezeiten meint DMRZ mit `WARTEN`? Beides zu
  // summieren wäre falsch: die Wartezeit zwischen Hin- und Rückfahrt steht an
  // beiden Fahrten und würde doppelt abgerechnet.
  waitSource: 'standby',
  // -1 = keine, 1 = voll, 2 = ermäßigt. Leer = aus der Verkehrsform ableiten.
  taxCode: ''
};

export const DMRZ_FORMAT_OPTIONS = {
  encoding: [
    { value: 'utf-8', label: 'UTF-8 (laut Spezifikation)' },
    { value: 'latin-1', label: 'ISO-8859-1 / Latin-1 (wie die Beispieldatei)' }
  ],
  quoteFields: [
    { value: 'true', label: 'Ja (laut Spezifikation)' },
    { value: 'false', label: 'Nein (wie die Beispieldatei)' }
  ],
  durationFormat: [
    { value: 'hhmm', label: 'hh:mm (laut Spezifikation)' },
    { value: 'minutes', label: 'Ganze Minuten (wie die Beispieldatei)' }
  ],
  headerVariant: [
    { value: 'spec', label: 'Saubere Spaltennamen' },
    { value: 'sample', label: 'Exakt wie die Beispieldatei' }
  ],
  waitSource: [
    { value: 'standby', label: 'Standzeit am Abholort (Ankunft bis Zustieg)' },
    { value: 'interTrip', label: 'Wartezeit zwischen Hin- und Rückfahrt' },
    { value: 'none', label: 'Keine Wartezeit übertragen' }
  ]
};

// ── kleine Helfer ────────────────────────────────────────────────────────────

const asText = (value) => (value === null || value === undefined ? '' : String(value));

const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value.seconds) return new Date(value.seconds * 1000);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const pad2 = (value) => String(value).padStart(2, '0');

/** tt.mm.jjjj — das einzige von DMRZ akzeptierte Datumsformat. */
const dmrzDate = (value) => {
  const date = toDate(value);
  if (!date) return '';
  return `${pad2(date.getDate())}.${pad2(date.getMonth() + 1)}.${date.getFullYear()}`;
};

/** hh:mm:ss wie in der Beispieldatei. */
const dmrzTime = (value) => {
  const date = toDate(value);
  if (!date) return '';
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`;
};

/** Dezimalzahl mit Komma — DMRZ erwartet deutsches Dezimalzeichen. */
const dmrzNumber = (value, digits = 2) => {
  if (value === null || value === undefined || value === '') return '';
  const number = Number(value);
  if (!Number.isFinite(number)) return '';
  return number.toFixed(digits).replace('.', ',');
};

const truncate = (value, maxLength) => {
  const text = asText(value).trim();
  return text.length > maxLength ? text.slice(0, maxLength) : text;
};

/**
 * Patientenname in Vor- und Nachname zerlegen. Wir führen den Namen in einem
 * Feld, DMRZ braucht zwei. „Musterfrau, Maria" ist eindeutig; sonst gilt das
 * letzte Wort als Nachname — bei Doppelnamen kann das danebenliegen, deshalb
 * meldet der Prüflauf solche Fahrten als „Name prüfen".
 */
export const splitPatientName = (fullName) => {
  const text = asText(fullName).trim().replace(/\s+/g, ' ');
  if (!text) return { firstName: '', lastName: '', ambiguous: false };

  if (text.includes(',')) {
    const [last, ...rest] = text.split(',');
    return {
      lastName: truncate(last, 47),
      firstName: truncate(rest.join(' '), 30),
      ambiguous: false
    };
  }

  const parts = text.split(' ');
  if (parts.length === 1) {
    return { lastName: truncate(parts[0], 47), firstName: '', ambiguous: true };
  }
  const lastName = parts[parts.length - 1];
  const firstName = parts.slice(0, -1).join(' ');
  return {
    lastName: truncate(lastName, 47),
    firstName: truncate(firstName, 30),
    // Mehr als zwei Wörter: „Anna Maria Schmidt" vs. „Anna von Schmidt" lässt
    // sich nicht sicher trennen.
    ambiguous: parts.length > 2
  };
};

/**
 * Freitext-Adresse in Straße / PLZ / Ort zerlegen. DMRZ hat für Abhol- und
 * Zielort *keine* eigene Hausnummernspalte, die Hausnummer bleibt deshalb in
 * `A_STRASSE`/`Z_STRASSE` — genau wie in der Beispieldatei („Maubisstraße 23").
 */
export const splitGermanAddress = (address) => {
  const text = asText(address).trim();
  if (!text) return { street: '', zip: '', city: '', country: 'DE', complete: false };

  // Länderangabe am Ende entfernen — Google liefert sie mit, DMRZ hat ein
  // eigenes Feld dafür.
  const withoutCountry = text.replace(/,\s*(Deutschland|Germany|DE)\s*$/i, '').trim();
  const parts = withoutCountry.split(',').map(part => part.trim()).filter(Boolean);

  let street = '';
  let zip = '';
  let city = '';

  const zipCityPattern = /^(\d{5})\s+(.+)$/;
  for (const part of parts) {
    const match = part.match(zipCityPattern);
    if (match && !zip) {
      zip = match[1];
      city = match[2];
    } else if (!street) {
      street = part;
    } else if (!city && !/^\d{5}$/.test(part)) {
      city = part;
    }
  }

  // Einzeiler ohne Komma: „Musterstr. 1 33106 Paderborn"
  if (!zip) {
    const inline = withoutCountry.match(/^(.*?)[\s,]+(\d{5})\s+(.+)$/);
    if (inline) {
      street = inline[1].trim();
      zip = inline[2];
      city = inline[3].trim();
    }
  }

  return {
    street: truncate(street, 60),
    zip: truncate(zip, 10),
    city: truncate(city, 40),
    country: 'DE',
    complete: Boolean(street && zip && city)
  };
};

/**
 * Kostenträger-IK auf 9 Stellen bringen. Kassen führen historisch 7-stellige
 * Kennungen; DMRZ erwartet die 9-stellige Form mit vorangestellter „10".
 */
export const normalizePayerIk = (value) => {
  const digits = asText(value).replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length === 7) return `10${digits}`;
  return digits;
};

/** Versichertenstatus: einstellige Angaben werden mit „0000" aufgefüllt. */
export const normalizeInsuranceStatus = (value) => {
  const digits = asText(value).replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length === 1) return `${digits}0000`;
  return digits.slice(0, 5);
};

const minutesBetweenDates = (from, to) => {
  const fromDate = toDate(from);
  const toDateValue = toDate(to);
  if (!fromDate || !toDateValue) return null;
  const diff = toDateValue.getTime() - fromDate.getTime();
  if (diff < 0) return null;
  return Math.round(diff / 60000);
};

const formatDuration = (minutes, format) => {
  if (minutes === null || minutes === undefined) return '';
  const total = Math.max(0, Math.round(minutes));
  if (format === 'minutes') return String(total);
  return `${pad2(Math.floor(total / 60))}:${pad2(total % 60)}`;
};

// ── Zeile bauen ──────────────────────────────────────────────────────────────

/**
 * Fahrtdauer. Bevorzugt der besetzte Abschnitt aus dem Tracking (das ist die
 * Zeit, die die Kasse bezahlt); ersatzweise Zustieg → Fahrtende.
 */
const resolveDurationMinutes = (ride, facts) => {
  if (Number.isFinite(facts.occupiedFromMs) && Number.isFinite(facts.occupiedToMs)) {
    const diff = Math.round((facts.occupiedToMs - facts.occupiedFromMs) / 60000);
    if (diff >= 0) return diff;
  }
  // Zustieg: die Fahrer-App schreibt passengerPickedUpTimestamp, ältere Portal-Fahrten
  // pickupTimestamp. Erst danach die Ankunft am Abholort als Notnagel — sie enthält die
  // Wartezeit vor dem Einsteigen und macht die Fahrt damit länger, als sie war.
  return minutesBetweenDates(
    ride.passengerPickedUpTimestamp || ride.pickupTimestamp || ride.arrivedTimestamp,
    ride.completedTimestamp || ride.completionTimestamp
  );
};

const resolveWaitMinutes = (facts, format) => {
  if (format.waitSource === 'none') return '';
  const value = format.waitSource === 'interTrip'
    ? facts.interTripWaitMinutes
    : facts.standbyMinutes;
  return Number.isFinite(value) ? String(Math.max(0, Math.round(value))) : '0';
};

/**
 * Umsatzsteuerschlüssel. Personenbeförderung im Taxi (Nahverkehr) ist ermäßigt
 * besteuert, Mietwagenverkehr voll — Grundlage ist die im Assistenten
 * hinterlegte Verkehrsform, überschreibbar über `dmrzFormat.taxCode`.
 */
const resolveTaxCode = (config, format) => {
  const override = asText(format.taxCode).trim();
  if (override) return override;
  return asText(config.transportMode) === 'mietwagen' ? '1' : '2';
};

/**
 * Zuzahlungsbetrag. Nur ein gepflegter Betrag aus der Verordnung geht raus —
 * ein aus unserem Tarifrechner abgeleiteter Wert ist eine Schätzung und hat in
 * einer Abrechnungsdatei nichts verloren (§8.7).
 */
const resolveCopayment = (facts) => {
  const copayment = facts.copayment || {};
  if (copayment.status === 'exempt') return '0,00';
  if (copayment.amountSource === 'order' && Number.isFinite(copayment.amountEur)) {
    return dmrzNumber(copayment.amountEur);
  }
  return '0,00';
};

/** WAGEN muss exakt der Fahrzeugbezeichnung im DMRZ-Zugang entsprechen. */
const resolveVehicleName = (ride, config) => {
  const plate = asText(ride.driverLicensePlate).trim();
  const map = config.dmrzVehicleNames || {};
  if (plate && map[plate]) return truncate(map[plate], 40);
  if (ride.driverVehicleId && map[ride.driverVehicleId]) {
    return truncate(map[ride.driverVehicleId], 40);
  }
  return truncate(plate, 40);
};

/** TPE1–TPE20: bis zu 20 Transporteigenschaften aus unseren Capabilities. */
const resolveTransportProperties = (facts, snapshot, config) => {
  const map = config.dmrzCapabilityMap || {};
  const source = Array.isArray(facts.capabilities) && facts.capabilities.length > 0
    ? facts.capabilities
    : (Array.isArray(snapshot.requiredCapabilities) ? snapshot.requiredCapabilities : []);

  const mapped = source
    .map(capability => asText(map[capability]).trim())
    // Nicht gemappte Eigenschaften bewusst weglassen: ein bei DMRZ unbekannter
    // Wert lässt den ganzen Import scheitern.
    .filter(Boolean);

  const unique = [...new Set(mapped)].slice(0, 20);
  return Array.from({ length: 20 }, (_, index) => unique[index] || '');
};

const flag = (value) => (value === true ? '1' : '');

/**
 * Baut die 76 Werte einer Zeile. Reihenfolge = DMRZ_COLUMNS.
 */
export const buildDmrzRow = (ride, config, format) => {
  const snapshot = ride.medicalTransportSnapshot || {};
  const facts = (ride.medicalTransportBillingFacts && typeof ride.medicalTransportBillingFacts === 'object')
    ? ride.medicalTransportBillingFacts
    : {};

  const name = splitPatientName(snapshot.patientName || ride.customerName);
  const pickup = splitGermanAddress(ride.start);
  const destination = splitGermanAddress(ride.destination);
  const serviceMoment = ride.completedTimestamp || ride.scheduledTime || ride.timestamp;

  const hasPrescriptionDate = Boolean(snapshot.prescriptionDate);
  const approvalFree = snapshot.approvalRequired === false
    || snapshot.approvalStatus === 'not_required';

  return [
    /*  1 */ truncate(ride.id, 20),
    /*  2 */ '',
    /*  3 */ name.lastName,
    /*  4 */ name.firstName,
    /*  5 */ dmrzDate(snapshot.patientBirthDate),
    /*  6 */ truncate(snapshot.patientStreet, 60),
    /*  7 */ truncate(snapshot.patientHouseNumber, 10),
    /*  8 */ truncate(snapshot.patientZip, 10),
    /*  9 */ truncate(snapshot.patientCity, 40),
    /* 10 */ truncate(snapshot.patientAddressAddition, 40),
    /* 11 */ truncate(snapshot.patientCountry || 'DE', 3),
    /* 12 */ normalizePayerIk(snapshot.healthInsuranceIk),
    /* 13 */ truncate(snapshot.insuranceNumber, 12),
    /* 14 */ normalizeInsuranceStatus(snapshot.insuranceStatus),
    /* 15 */ dmrzDate(serviceMoment),
    /* 16 */ dmrzTime(serviceMoment),
    /* 17 */ formatDuration(resolveDurationMinutes(ride, facts), format.durationFormat),
    /* 18 */ resolveVehicleName(ride, config),
    /* 19 */ dmrzNumber(facts.occupiedKm),
    /* 20 */ resolveWaitMinutes(facts, format),
    // GESAMT: DMRZ bewertet aus dem Kassenvertrag — wir liefern keine Preise.
    /* 21 */ '0,00',
    /* 22 */ resolveCopayment(facts),
    /* 23 */ resolveTaxCode(config, format),
    /* 24 */ pickup.street,
    /* 25 */ pickup.zip,
    /* 26 */ pickup.city,
    /* 27 */ '',
    /* 28 */ pickup.country,
    /* 29 */ destination.street,
    /* 30 */ destination.zip,
    /* 31 */ destination.city,
    /* 32 */ '',
    /* 33 */ destination.country,
    /* 34 */ '1',
    // Nur die Rückfahrt verweist auf die Hinfahrt — sonst zeigen beide
    // Zeilen aufeinander und DMRZ kann das Paar nicht auflösen.
    /* 35 */ facts.tripDirection === 'return' ? truncate(facts.linkedRideId, 20) : '',
    /* 36 */ truncate(ride.medicalTransportProofNotes || ride.billingNotes, 255),
    /* 37 */ '1',
    /* 38 */ truncate(ride.recurringOrderId, 20),
    /* 39 */ config.dmrzUseLocationIk ? truncate(config.ownIk, 9) : '',
    /* 40 */ config.dmrzUseLocationIk ? truncate(config.operatorName, 60) : '',
    /* 41 */ dmrzDate(snapshot.prescriptionDate),
    /* 42 */ hasPrescriptionDate ? '' : '1',
    /* 43 */ asText(snapshot.copaymentDocumentMode || '0'),
    /* 44 */ flag(snapshot.prescriptionMarkBvg),
    /* 45 */ flag(snapshot.prescriptionMarkAccident),
    /* 46 */ flag(snapshot.prescriptionMarkWorkAccident),
    /* 47 */ flag(snapshot.prescriptionMarkOther),
    /* 48 */ truncate(snapshot.practiceNumber, 9),
    /* 49 */ truncate(snapshot.physicianNumber, 9),
    /* 50 */ approvalFree ? '1' : '',
    /* 51 */ dmrzDate(snapshot.approvalApprovedAt),
    /* 52 */ dmrzDate(snapshot.validFrom),
    /* 53 */ dmrzDate(snapshot.approvalValidUntil || snapshot.validUntil),
    /* 54 */ truncate(snapshot.approvalNumber, 20),
    /* 55 */ truncate(snapshot.approvalKind, 2),
    /* 56 */ truncate(config.dmrzTransportCategory, 40),
    /* 57.. */ ...resolveTransportProperties(facts, snapshot, config)
  ];
};

// ── Vorabprüfung ─────────────────────────────────────────────────────────────

/**
 * Was würde DMRZ an dieser Zeile beanstanden? Bewusst *vor* dem Download, denn
 * ein abgelehnter Import kostet den Unternehmer eine komplette Runde: Datei
 * hochladen, Fehlerliste lesen, bei uns korrigieren, neu exportieren.
 *
 * Die Liste blockiert nichts — sie benennt nur, was fehlt.
 */
export const collectDmrzIssues = (rides, config) => {
  const issues = [];
  const add = (key, label, hint, rideIds) => {
    if (rideIds.length > 0) issues.push({ key, label, hint, rideIds });
  };

  const idsWhere = (predicate) => rides.filter(predicate).map(ride => ride.id);

  add(
    'payerIk',
    'Kostenträgerkennung (IK) fehlt',
    'Steht auf Muster 4 in der Kopfzeile. Ohne IK ordnet DMRZ den Fall keiner Kasse zu.',
    idsWhere(ride => !normalizePayerIk((ride.medicalTransportSnapshot || {}).healthInsuranceIk))
  );
  add(
    'insuranceNumber',
    'Versichertennummer fehlt',
    'Pflichtangabe für die Abrechnung nach § 302 SGB V.',
    idsWhere(ride => !(ride.medicalTransportSnapshot || {}).insuranceNumber)
  );
  add(
    'patientAddress',
    'Patientenanschrift unvollständig',
    'DMRZ führt Straße, Hausnummer, PLZ und Ort getrennt — nachtragbar in der Verordnung.',
    idsWhere(ride => {
      const snapshot = ride.medicalTransportSnapshot || {};
      return !(snapshot.patientStreet && snapshot.patientZip && snapshot.patientCity);
    })
  );
  add(
    'pickupAddress',
    'Abholadresse nicht zerlegbar',
    'Straße ist Pflichtfeld. Adressen ohne PLZ/Ort können wir nicht sauber aufteilen.',
    idsWhere(ride => !splitGermanAddress(ride.start).complete)
  );
  add(
    'destinationAddress',
    'Zieladresse nicht zerlegbar',
    'Straße ist Pflichtfeld. Adressen ohne PLZ/Ort können wir nicht sauber aufteilen.',
    idsWhere(ride => !splitGermanAddress(ride.destination).complete)
  );
  add(
    'patientName',
    'Vor-/Nachname nicht eindeutig trennbar',
    'DMRZ braucht zwei Felder. Schreiben Sie den Namen in der Verordnung als „Nachname, Vorname".',
    idsWhere(ride => splitPatientName((ride.medicalTransportSnapshot || {}).patientName).ambiguous)
  );
  add(
    'occupiedKm',
    'Besetzt-Kilometer fehlen',
    'Werden aus dem Fahrertracking ermittelt. „Neu berechnen" im Tab „Fahrten" hilft.',
    idsWhere(ride => {
      const facts = ride.medicalTransportBillingFacts || {};
      return !Number.isFinite(facts.occupiedKm);
    })
  );
  add(
    'vehicleName',
    'Fahrzeug ohne DMRZ-Bezeichnung',
    'WAGEN muss exakt der Fahrzeugbezeichnung im DMRZ-Zugang entsprechen — im Tab „Abrechnungszentrum" zuordnen.',
    idsWhere(ride => !resolveVehicleName(ride, config))
  );
  add(
    'prescriptionDate',
    'Verordnungsdatum fehlt',
    'Ohne Datum meldet der Export „keine Verordnung vorhanden" — das führt bei genehmigungspflichtigen Fahrten zur Absetzung.',
    idsWhere(ride => !(ride.medicalTransportSnapshot || {}).prescriptionDate)
  );

  if (!asText(config.dmrzTransportCategory).trim()) {
    issues.push({
      key: 'transportCategory',
      label: 'Transportkategorie nicht hinterlegt',
      hint: 'Die Kategorie muss im DMRZ-Zugang existieren — im Tab „Abrechnungszentrum" eintragen.',
      rideIds: [],
      global: true
    });
  }

  return issues;
};

// ── Datei erzeugen ───────────────────────────────────────────────────────────

const escapeField = (value, quoteFields) => {
  // Trennzeichen und Zeilenumbrüche fliegen IMMER raus, auch bei aktivem
  // Quoting: Ob DMRZ Anführungszeichen tatsächlich auswertet, ist offen (die
  // Beispieldatei nutzt gar keine). Ein Semikolon aus einem Notizfeld würde
  // sonst die ganze Zeile verschieben — im Zweifel lieber ein Leerzeichen
  // weniger im Freitext als eine abgewiesene Abrechnungsdatei.
  const text = asText(value)
    .replaceAll(';', ' ')
    .replaceAll('\r', '')
    .replaceAll('\n', ' ');
  if (!quoteFields) return text;
  return `"${text.replaceAll('"', '""')}"`;
};

/**
 * Latin-1-Bytes erzeugen. Der Browser kann von Haus aus nur UTF-8 kodieren —
 * für die Beispieldatei-Variante bilden wir Zeichen > U+00FF auf „?" ab, statt
 * sie stillschweigend zu verlieren.
 */
const encodeLatin1 = (text) => {
  const bytes = new Uint8Array(text.length);
  for (let index = 0; index < text.length; index += 1) {
    const code = text.charCodeAt(index);
    bytes[index] = code <= 0xff ? code : 0x3f;
  }
  return bytes;
};

export const buildDmrzCsv = (rides, config = {}, formatOverrides = {}) => {
  const format = { ...DEFAULT_DMRZ_FORMAT, ...formatOverrides };
  const quoteFields = format.quoteFields !== false && format.quoteFields !== 'false';

  const header = DMRZ_COLUMNS.map(column => (
    format.headerVariant === 'sample' ? (SAMPLE_HEADER_OVERRIDES[column] || column) : column
  ));

  const lines = [header, ...rides.map(ride => buildDmrzRow(ride, config, format))]
    .map(values => values.map(value => escapeField(value, quoteFields)).join(';'));

  // Zeilenende CRLF — die Beispieldatei nutzt es und Windows-Importer sind
  // erfahrungsgemäß empfindlicher als Unix-Werkzeuge.
  const text = `${lines.join('\r\n')}\r\n`;

  if (format.encoding === 'latin-1') {
    return {
      bytes: encodeLatin1(text),
      mimeType: 'text/csv;charset=iso-8859-1;',
      rowCount: rides.length
    };
  }

  // Kein BOM: DMRZ erwartet reines UTF-8, ein BOM landet sonst im ersten
  // Spaltennamen.
  return {
    bytes: new TextEncoder().encode(text),
    mimeType: 'text/csv;charset=utf-8;',
    rowCount: rides.length
  };
};

export const dmrzFileName = (date = new Date()) => {
  const stamp = `${date.getFullYear()}${pad2(date.getMonth() + 1)}${pad2(date.getDate())}`;
  return `dmrz-fahrtenimport-${stamp}.csv`;
};
